import {cva} from 'class-variance-authority';
import {cn} from '../lib/utils';
import {getEmailInitials} from '../lib/get-initials';
import {Avatar, AvatarFallback, Button} from '../primitives';

export type CanvasHeaderPages =
  | 'hidden'
  | {open: boolean; onToggle: () => void};

export interface CanvasHeaderViewer {
  email: string;
}

interface Props {
  pages?: CanvasHeaderPages;
  viewer?: CanvasHeaderViewer | null;
  className?: string;
}

const pagesButtonVariants = cva('transition-colors', {
  variants: {
    pressed: {
      true: 'border-border bg-muted shadow-none hover:bg-muted',
      false: '',
    },
  },
  defaultVariants: {pressed: false},
});

// Deliberately unbranded: the page body carries the firm's identity, and firms
// with sub-brands pick a logo per page. h-16 so the panels can offset off a
// round number.
export const CanvasHeader = ({pages = 'hidden', viewer, className}: Props) => {
  const toggle = typeof pages === 'object' ? pages : null;

  return (
    <header
      className={cn(
        'flex h-16 items-center gap-4 border-b border-border bg-background px-6',
        className,
      )}
    >
      {toggle && (
        <Button
          variant="outline"
          size="lg"
          aria-pressed={toggle.open}
          onClick={toggle.onToggle}
          className={pagesButtonVariants({pressed: toggle.open})}
        >
          All pages
        </Button>
      )}
      {viewer && (
        <div
          className="ml-auto flex min-w-0 items-center gap-2 text-sm text-muted-foreground"
          title={viewer.email}
        >
          <Avatar className="size-6 bg-muted text-xxs text-muted-foreground">
            <AvatarFallback>{getEmailInitials(viewer.email)}</AvatarFallback>
          </Avatar>
          <span className="hidden truncate sm:inline">{viewer.email}</span>
        </div>
      )}
    </header>
  );
};
